import React, { useEffect, useState } from 'react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export const CraftInspector: React.FC = () => {
  const [fps, setFps] = useState(120);
  const [scrollVelocity, setScrollVelocity] = useState(0);
  const [activeTriggers, setActiveTriggers] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFallbackMode, setIsFallbackMode] = useState(false);

  useEffect(() => {
    const hasNoWebglFlag =
      typeof window !== 'undefined' &&
      (window.location.search.includes('nowebgl') || window.location.hash.includes('nowebgl'));
    setIsFallbackMode(hasNoWebglFlag);

    let frameCount = 0;
    let lastTime = performance.now();
    let animId: number;

    const measureFps = (now: number) => {
      frameCount++;
      if (now >= lastTime + 500) {
        const calculatedFps = Math.round((frameCount * 1000) / (now - lastTime));
        setFps(Math.min(120, calculatedFps));
        frameCount = 0;
        lastTime = now;
      }
      animId = requestAnimationFrame(measureFps);
    };

    animId = requestAnimationFrame(measureFps);

    let lastScrollY = window.scrollY;
    let lastScrollTime = performance.now();

    const scrollInterval = setInterval(() => {
      const currentScrollY = window.scrollY;
      const currentTime = performance.now();
      const dt = Math.max(1, currentTime - lastScrollTime);
      const dy = Math.abs(currentScrollY - lastScrollY);
      const velocity = Math.round((dy / dt) * 100);

      setScrollVelocity(velocity);
      lastScrollY = currentScrollY;
      lastScrollTime = currentTime;

      const triggers = ScrollTrigger.getAll().length;
      setActiveTriggers(triggers);
    }, 200);

    return () => {
      cancelAnimationFrame(animId);
      clearInterval(scrollInterval);
    };
  }, []);

  return (
    <div className="fixed bottom-4 left-4 z-40 font-mono text-[11px] select-none pointer-events-auto">
      {isExpanded ? (
        <div className="rounded-lg border border-carbon-900/15 bg-white/95 backdrop-blur-xl p-3.5 text-carbon-900 shadow-xl space-y-2 w-64">
          <div className="flex items-center justify-between border-b border-carbon-900/10 pb-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-chlorophyll" />
              <span className="font-bold text-carbon-900 tracking-wider">GAIA ARCHIVE // HUD</span>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-carbon-900/50 hover:text-carbon-900 transition-colors px-1"
              aria-label="Collapse Telemetry HUD"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <span className="text-carbon-900/50 block">FRAME RATE</span>
              <span className={`font-bold ${fps >= 55 ? 'text-chlorophyll-deep' : 'text-solar-amber'}`}>
                {fps} FPS
              </span>
            </div>

            <div>
              <span className="text-carbon-900/50 block">PIPELINE</span>
              <span className="font-bold text-carbon-800">
                {isFallbackMode ? 'SVG_CONTOUR' : 'WEBGL // BIOME 3D'}
              </span>
            </div>

            <div>
              <span className="text-carbon-900/50 block">SCROLL VELOCITY</span>
              <span className="font-bold text-carbon-800">{scrollVelocity} px/s</span>
            </div>

            <div>
              <span className="text-carbon-900/50 block">GSAP TRIGGERS</span>
              <span className="font-bold text-carbon-800">{activeTriggers} ACTIVE</span>
            </div>

            <div className="col-span-2 pt-1 border-t border-carbon-900/10">
              <span className="text-carbon-900/50 block">PAGE CARBON FOOTPRINT</span>
              <span className="font-bold text-chlorophyll-deep">~0.014g CO2 / AUDIT</span>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-carbon-900/15 bg-white/90 backdrop-blur-md text-carbon-900 hover:border-chlorophyll hover:text-chlorophyll-deep transition-all shadow-md active:scale-95"
          data-cursor="interactive"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-chlorophyll" />
          <span className="font-semibold tracking-wider text-[10px]">
            {fps} FPS • {isFallbackMode ? 'TOPOGRAPHY' : 'BIOME 3D'}
          </span>
        </button>
      )}
    </div>
  );
};
